import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertTriangle, HelpCircle, FileText, Download, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO, { generateHowToSchema } from '../components/SEO';
import { guideAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { getField, getSteps } from '../lib/localize';
import { trackGuideView, trackGuideDownload } from '../hooks/useAnalytics';

const GuideDetail = () => {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  useEffect(() => {
    const fetchGuide = async () => {
      try {
        const res = await guideAPI.getBySlug(slug);
        setGuide(res.data);
        // Track guide view
        trackGuideView(res.data.id);
      } catch (err) {
        console.error('Failed to fetch guide:', err);
      }
      setLoading(false);
    };
    fetchGuide();
  }, [slug]);

  const categoryLabels = {
    documentation: t('categories.documentation'),
    legal: t('categories.legal'),
    fees: t('categories.fees'),
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
        <Navbar isDark={isDark} />
        <div className="pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse">
              <div className={`h-8 rounded w-1/4 mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <div className={`h-12 rounded w-3/4 mb-6 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <div className={`h-4 rounded w-full mb-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <div className={`h-4 rounded w-5/6 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!guide) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
        <Navbar isDark={isDark} />
        <div className="pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('guide.notFound')}</h1>
            <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('guide.notFound')}</p>
            <Link to="/guides">
              <Button className="bg-[#2E7D32] hover:bg-[#1B5E20]">
                <ArrowLeft size={16} className="mr-2" />
                {t('guide.backToHub')}
              </Button>
            </Link>
          </div>
        </div>
        <Footer isDark={isDark} />
      </div>
    );
  }

  const guideTitle = getField(guide, 'title', language);
  const guideSummary = getField(guide, 'summary', language);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      <SEO 
        title={guideTitle}
        description={guideSummary}
        path={`/guides/${slug}`}
        type="article"
        article={{
          publishedTime: guide.created_at,
          modifiedTime: guide.updated_at,
          section: categoryLabels[guide.category] || 'Land Information',
          tags: ['নামজারি', 'খতিয়ান', 'জমি রেজিস্ট্রেশন', 'Bangladesh Land'],
        }}
        schema={generateHowToSchema({
          title: guideTitle,
          summary: guideSummary,
          steps: getSteps(guide, language),
        })}
      />
      <Navbar isDark={isDark} />

      {/* Header */}
      <section className={`pt-24 pb-8 border-b ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/guides" 
            className={`inline-flex items-center mb-6 ${isDark ? 'text-gray-400 hover:text-[#2E7D32]' : 'text-gray-600 hover:text-[#2E7D32]'}`}
            data-testid="back-link"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('guide.backToHub')}
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className={`text-xs uppercase tracking-wider font-medium px-3 py-1 rounded-full ${isDark ? 'text-[#FBC02D] bg-[#FBC02D]/10' : 'text-[#2E7D32] bg-[#E8F5E9]'}`}>
              {categoryLabels[guide.category]}
            </span>
          </div>

          <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="guide-title">
            {getField(guide, 'title', language)}
          </h1>
          <p className={`text-lg mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{getField(guide, 'summary', language)}</p>
          
          {/* Download PDF Button */}
          <a 
            href={guideAPI.getPdf(slug)} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex"
            onClick={() => trackGuideDownload(guide.id)}
          >
            <Button 
              className="bg-[#FBC02D] hover:bg-[#F9A825] text-gray-900 rounded-full font-semibold"
              data-testid="download-pdf-btn"
            >
              <FileDown size={18} className="mr-2" />
              {t('guide.downloadPdf')}
            </Button>
          </a>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Image */}
          {guide.images && guide.images.length > 0 && (
            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={guide.images[0]}
                alt={guide.title}
                className="w-full h-64 sm:h-80 object-cover"
              />
            </div>
          )}

          {/* Steps */}
          <div className="space-y-6 mb-12">
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('guide.steps')}</h2>
            {getSteps(guide, language).map((step) => (
              <div
                key={step.step_number}
                className={`rounded-xl p-6 border transition-colors ${isDark ? 'bg-[#111] border-white/10 hover:border-[#2E7D32]' : 'bg-white border-gray-200 hover:border-[#2E7D32]'}`}
                data-testid={`step-${step.step_number}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#2E7D32] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{step.step_number}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{step.title}</h3>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tips Section */}
          <div className={`rounded-xl p-6 mb-8 ${isDark ? 'bg-[#2E7D32]/10' : 'bg-[#E8F5E9]'}`}>
            <div className="flex items-start gap-4">
              <CheckCircle className="text-[#2E7D32] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('guide.tips')}</h3>
                <ul className={`space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>{language === 'en' ? '• Keep photocopies of all documents with you' : '• সকল কাগজপত্রের ফটোকপি সাথে রাখুন'}</li>
                  <li>{language === 'en' ? '• Know the required fees before visiting government offices' : '• সরকারি অফিসে যাওয়ার আগে প্রয়োজনীয় ফি জেনে নিন'}</li>
                  <li>{language === 'en' ? '• Consult an experienced professional' : '• অভিজ্ঞ কারো পরামর্শ নিন'}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Warning Section */}
          <div className={`rounded-xl p-6 mb-8 border ${isDark ? 'bg-[#FBC02D]/10 border-[#FBC02D]/30' : 'bg-[#FFFDE7] border-[#FBC02D]'}`}>
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-[#F9A825] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('guide.warning')}</h3>
                <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                  {language === 'en' 
                    ? 'Before buying land, always verify the deed and khatian. Consult an experienced lawyer to avoid fraud.'
                    : 'জমি কেনার আগে অবশ্যই দলিল ও খতিয়ান যাচাই করুন। প্রতারণা এড়াতে অভিজ্ঞ উকিলের সাথে পরামর্শ করুন।'}
                </p>
              </div>
            </div>
          </div>

          {/* PDFs */}
          {guide.pdfs && guide.pdfs.length > 0 && (
            <div className="mb-8">
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('guide.downloadPdf')}</h3>
              <div className="space-y-2">
                {guide.pdfs.map((pdf, idx) => (
                  <a
                    key={`pdf-${idx}`}
                    href={pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${isDark ? 'bg-[#111] border-white/10 hover:border-[#2E7D32]' : 'bg-white border-gray-200 hover:border-[#2E7D32]'}`}
                  >
                    <Download className="text-[#2E7D32]" size={20} />
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>ডকুমেন্ট {idx + 1}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-[#0D330F] rounded-2xl p-8 text-center">
            <HelpCircle className="mx-auto text-[#FBC02D] mb-4" size={40} />
            <h3 className="text-2xl font-bold text-white mb-3">{t('guide.needHelp')}</h3>
            <p className="text-gray-300 mb-6">
              {t('guide.helpDesc')}
            </p>
            <Link to="/services">
              <Button className="bg-[#FBC02D] hover:bg-[#F9A825] text-gray-900 rounded-full px-8 py-3 font-semibold">
                {t('landing.findProviders')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer isDark={isDark} />
    </div>
  );
};

export default GuideDetail;
